/** Coffee-only estimates. No quote, payment, credit decision, or reward is persisted. */
export type CoffeePriceBand = { minQuantity: number; maxQuantity: number; price: number };
export type ComparisonCoffee = {
  id: string;
  productId?: string;
  name: string;
  originName?: string | null;
  inventoryStatus?: string;
  unitWeight: number;
  maxQuantity: number;
  prices: CoffeePriceBand[];
  lastQuantity: number | null;
  monthlyQuantity: number | null;
  currentQuantity?: number | null;
  fixedPrice: boolean;
};

export type ComparisonBaseline = "last" | "monthly" | "checkout" | "custom";

/** Convert real historical pounds to whole packages when the customer changes lots. */
export function comparisonBaselines(coffee: ComparisonCoffee, history: { lastOrderPounds?: number | null; monthlyPounds?: number | null }) {
  const byWeight = (pounds: number | null | undefined) => typeof pounds === "number" && Number.isFinite(pounds) && pounds > 0 && coffee.unitWeight > 0 ? pounds / coffee.unitWeight : null;
  return [
    { value: "last" as const, quantity: coffee.lastQuantity ?? byWeight(history.lastOrderPounds), label: coffee.lastQuantity !== null ? "Last purchase · this coffee" : "Last order · by weight" },
    { value: "monthly" as const, quantity: coffee.monthlyQuantity ?? byWeight(history.monthlyPounds), label: coffee.monthlyQuantity !== null ? "90-day monthly average · this coffee" : "90-day monthly average · by weight" },
    { value: "checkout" as const, quantity: coffee.currentQuantity ?? null, label: "This checkout" },
  ].flatMap((source) => source.quantity !== null && Number.isFinite(source.quantity) && source.quantity > 0 && Math.ceil(source.quantity) <= coffee.maxQuantity
    ? [{ ...source, quantity: Math.ceil(source.quantity) }] : []);
}

const money = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/** Catalog presentation only; do not disturb the history-based default selection. */
export function comparisonOriginGroups(coffees: ComparisonCoffee[]) {
  const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });
  const groups = new Map<string | null, ComparisonCoffee[]>();
  for (const coffee of coffees) {
    const origin = coffee.originName?.trim() || null;
    const rows = groups.get(origin) ?? [];
    rows.push(coffee);
    groups.set(origin, rows);
  }
  return Array.from(groups, ([origin, coffees]) => ({ origin, coffees: [...coffees].sort((a, b) =>
    collator.compare(a.name, b.name) || collator.compare(a.inventoryStatus ?? "", b.inventoryStatus ?? "") || collator.compare(a.id, b.id)) }))
    .sort((a, b) => a.origin === null ? 1 : b.origin === null ? -1 : collator.compare(a.origin, b.origin));
}

export type FinancingTerms = { monthlyRatePercent: number; months: number };

/** Hypothetical simple interest on the full coffee balance, not a lender quote. */
export function estimateFinancingInterest(principal: number, terms: FinancingTerms) {
  if (!Number.isFinite(principal) || principal < 0 ||
    !Number.isFinite(terms.monthlyRatePercent) || terms.monthlyRatePercent < 0 || terms.monthlyRatePercent > 100 ||
    !Number.isSafeInteger(terms.months) || terms.months < 1 || terms.months > 120) {
    throw new Error("Enter monthly interest from 0–100% and 1–120 whole months.");
  }
  return money(principal * terms.monthlyRatePercent / 100 * terms.months);
}

export function coffeePriceAtQuantity(prices: CoffeePriceBand[], quantity: number): number {
  if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error("Enter a positive whole quantity.");
  const band = prices.find((row) => quantity >= row.minQuantity && quantity <= row.maxQuantity);
  if (!band || !Number.isFinite(band.price) || band.price <= 0) throw new Error("Pricing is unavailable for this quantity.");
  return band.price;
}

export function compareCreditKeyCoffee(input: {
  coffee: ComparisonCoffee;
  usualQuantity: number;
  bulkQuantity: number;
  financingCost: number | null;
  financingTerms?: FinancingTerms;
  achDiscountRate: number;
  pointValue: number;
  creditKeyContractDiscountRate?: number;
  allocations?: Array<{coffee: ComparisonCoffee; quantity: number}>;
}) {
  const { coffee, usualQuantity, bulkQuantity, achDiscountRate, pointValue } = input;
  let financingCost = input.financingCost;
  if (input.financingTerms && financingCost !== null) throw new Error("Choose one financing estimate method.");
  const contractDiscountRate = input.creditKeyContractDiscountRate ?? 0;
  if (![usualQuantity, bulkQuantity].every((q) => Number.isSafeInteger(q) && q >= 1 && q <= coffee.maxQuantity) ||
    bulkQuantity < usualQuantity || !Number.isFinite(coffee.unitWeight) || coffee.unitWeight <= 0 ||
    !Number.isFinite(achDiscountRate) || achDiscountRate < 0 || achDiscountRate >= 1 ||
    !Number.isFinite(pointValue) || pointValue < 0 || pointValue >= 1 ||
    !Number.isFinite(contractDiscountRate) || contractDiscountRate < 0 || contractDiscountRate >= 1 ||
    (financingCost !== null && (!Number.isFinite(financingCost) || financingCost < 0 || financingCost > 1_000_000))) {
    throw new Error("Check the quantities and financing cost before comparing.");
  }

  const allocations = input.allocations ?? [{coffee, quantity: bulkQuantity}];
  if (allocations.some(row => !Number.isSafeInteger(row.quantity) || row.quantity < 1 || row.coffee.unitWeight !== coffee.unitWeight || (row.coffee.id !== coffee.id && (!coffee.productId || row.coffee.productId !== coffee.productId || row.coffee.fixedPrice || coffee.fixedPrice))) || allocations.reduce((sum,row)=>sum+row.quantity,0) !== bulkQuantity) throw new Error("Choose quantities of the same product to combine.");
  function purchase(rows: typeof allocations, discountRate: number) {
    const quantity = rows.reduce((sum,row)=>sum+row.quantity,0);
    const subtotal = money(rows.reduce((sum,row)=>sum+money(coffeePriceAtQuantity(row.coffee.prices,quantity)*row.quantity*row.coffee.unitWeight),0));
    const discount = money(subtotal * discountRate);
    const total = money(subtotal - discount);
    const points = Math.round(total);
    const rewards = money(points * pointValue);
    return { subtotal, discount, total, points, rewards, effective: money(total - rewards), price: subtotal/(quantity*coffee.unitWeight) };
  }
  const fullOrders = Math.floor(bulkQuantity / usualQuantity);
  const remainder = bulkQuantity % usualQuantity;
  // Use exactly the same cargo quantities on both sides; each smaller order gets
  // its own product tier, including orders that straddle two cargos.
  const repeated = {subtotal:0,discount:0,total:0,points:0,rewards:0};
  let source=0, used=0;
  for(let remaining=bulkQuantity; remaining>0;) {
    let count=Math.min(usualQuantity,remaining);
    remaining-=count;
    const rows: typeof allocations=[];
    while(count>0) {
      const row=allocations[source];
      if (!row) throw new Error("Cargo quantities do not cover the comparison.");
      const take=Math.min(count,row.quantity-used);
      rows.push({coffee:row.coffee,quantity:take});
      count-=take; used+=take;
      if(used===row.quantity){source++;used=0;}
    }
    const order=purchase(rows,achDiscountRate);
    for(const key of ["subtotal","discount","total","points","rewards"] as const) repeated[key]=money(repeated[key]+order[key]);
  }
  const recurring = { ...repeated, effective: money(repeated.total - repeated.rewards) };
  const creditKey = purchase(allocations, contractDiscountRate);
  if (input.financingTerms) financingCost = estimateFinancingInterest(creditKey.total, input.financingTerms);
  const bulkAch = purchase(allocations, achDiscountRate);
  const pounds = bulkQuantity * coffee.unitWeight;
  const financingIncluded = financingCost !== null;
  const creditKeyEffective = money(creditKey.effective + (financingCost ?? 0));
  return {
    recurring, creditKey, bulkAch, pounds, fullOrders, remainder,
    purchaseCount: fullOrders + (remainder ? 1 : 0),
    volumeSaving: money(recurring.subtotal - creditKey.subtotal),
    creditKeyEffective, financingIncluded, financingCost,
    difference: money(recurring.effective - creditKeyEffective),
    breakEvenFinancingCost: money(recurring.effective - creditKey.effective),
    recurringPerPound: recurring.effective / pounds,
    creditKeyPerPound: creditKeyEffective / pounds,
    bulkAchPerPound: bulkAch.effective / pounds,
  };
}
